import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { Box, TextField, IconButton, Typography, CircularProgress } from "@material-ui/core";
import { Edit, Delete, Check, Close } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { fetchTaskPage, createTask, updateTask, deleteTask } from "store/slices/job";
import AppColor from "utilities/AppColor";
import AppButton from "components/CustomButtons/AppButton";
import AppConfirmDialog from "components/Dialog/AppConfirmDialog";

const useStyles = makeStyles(() => ({
  container: {
    marginTop: "1.5rem",
    padding: "1rem",
    borderRadius: "6px",
    backgroundColor: AppColor.greyLighten5,
    border: `1px solid ${AppColor.greyLighten4}`,
  },
  header: {
    fontWeight: "bold",
    marginBottom: "1rem",
    fontSize: "14px",
    color: AppColor.greyDarker,
    borderBottom: `2px solid ${AppColor.mainColor}`,
    paddingBottom: "4px",
    display: "inline-block",
  },
  inputRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: "1rem",
    gap: "10px",
  },
  input: {
    flexGrow: 1,
    "& .MuiInputBase-input": {
      fontSize: "14px",
      padding: "10px 14px",
    },
  },
  taskRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    marginBottom: "6px",
    backgroundColor: AppColor.white,
    borderRadius: "4px",
    border: `1px solid ${AppColor.greyLighten3}`,
    transition: "box-shadow 0.2s",
    "&:hover": {
      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    },
  },
  taskText: {
    fontSize: "14px",
    color: AppColor.black,
    wordBreak: "break-all",
  },
  taskEditRow: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    gap: "8px",
  },
  actionButtons: {
    display: "flex",
    alignItems: "center",
  },
  iconButton: {
    padding: "6px",
    marginLeft: "4px",
  },
  editIcon: {
    color: AppColor.orange,
    fontSize: "18px",
  },
  deleteIcon: {
    color: AppColor.red,
    fontSize: "18px",
  },
  checkIcon: {
    color: AppColor.mainColor,
    fontSize: "18px",
  },
  closeIcon: {
    color: AppColor.greyDark,
    fontSize: "18px",
  },
  noTasks: {
    fontStyle: "italic",
    color: AppColor.greyDark,
    fontSize: "13px",
    textAlign: "center",
    padding: "1rem 0",
  },
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "1.5rem 0",
  },
}));

const ShiftTasksSection = ({ jobId, activeAvailabilityIds, isDisabled }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newTaskName, setNewTaskName] = useState("");
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTaskName, setEditingTaskName] = useState("");
  const [taskToDelete, setTaskToDelete] = useState(null);

  const loadTasks = async () => {
    if (!activeAvailabilityIds || activeAvailabilityIds.length === 0) return;
    setLoading(true);
    try {
      const result = await dispatch(
        fetchTaskPage({
          jobId,
          jobAvailabilityId: activeAvailabilityIds[0],
          status: "ACTIVE",
          size: 100,
        })
      );
      if (result.payload && result.payload.content) {
        setTasks(result.payload.content);
      }
    } catch (error) {
      console.error("Failed to load tasks", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, [jobId, activeAvailabilityIds[0]]);

  const handleAddTask = async () => {
    if (!newTaskName.trim() || submitting || activeAvailabilityIds.length === 0) return;

    setSubmitting(true);
    try {
      await dispatch(
        createTask({
          tasksName: newTaskName.trim(),
          jobId,
          jobAvailabilityId: activeAvailabilityIds[0],
        })
      );
      setNewTaskName("");
      await loadTasks();
    } catch (error) {
      console.error("Failed to add task", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartEdit = (task) => {
    setEditingTaskId(task.id);
    setEditingTaskName(task.tasksName);
  };

  const handleCancelEdit = () => {
    setEditingTaskId(null);
    setEditingTaskName("");
  };

  const handleUpdateTask = async (taskToUpdate) => {
    if (!editingTaskName.trim() || submitting) return;

    setSubmitting(true);
    try {
      await dispatch(
        updateTask({
          id: taskToUpdate.id,
          tasksName: editingTaskName.trim(),
        })
      );
      setEditingTaskId(null);
      setEditingTaskName("");
      await loadTasks();
    } catch (error) {
      console.error("Failed to update task", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTask = async (taskToDelete) => {
    if (submitting) return;

    setSubmitting(true);
    try {
      await dispatch(
        deleteTask({
          id: taskToDelete.id,
        })
      );
      await loadTasks();
    } catch (error) {
      console.error("Failed to delete task", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box className={classes.container}>
      <Typography className={classes.header}>
        {t("label.tasks", "Tasks")}
      </Typography>

      {!isDisabled && (
        <Box className={classes.inputRow}>
          <TextField
            className={classes.input}
            variant="outlined"
            size="small"
            placeholder={t("label.taskPlaceholder", "Enter task name...")}
            value={newTaskName}
            onChange={(e) => setNewTaskName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddTask();
              }
            }}
            disabled={submitting}
          />
          <AppButton
            type="button"
            onClick={handleAddTask}
            label={t("button.addTask", "Add")}
            buttonColor={AppColor.mainColor}
            disabled={!newTaskName.trim() || submitting}
            isLoading={submitting}
            style={{ margin: 0, height: "40px" }}
          />
        </Box>
      )}

      {loading ? (
        <Box className={classes.loaderContainer}>
          <CircularProgress size={24} style={{ color: AppColor.mainColor }} />
        </Box>
      ) : tasks.length === 0 ? (
        <Typography className={classes.noTasks}>
          {t("label.noTasks", "No tasks added to this shift yet")}
        </Typography>
      ) : (
        <Box>
          {tasks.map((task) => (
            <Box key={task.id} className={classes.taskRow}>
              {editingTaskId === task.id ? (
                <Box className={classes.taskEditRow}>
                  <TextField
                    className={classes.input}
                    variant="outlined"
                    size="small"
                    value={editingTaskName}
                    onChange={(e) => setEditingTaskName(e.target.value)}
                    disabled={submitting}
                    autoFocus
                  />
                  <IconButton
                    className={classes.iconButton}
                    onClick={() => handleUpdateTask(task)}
                    disabled={!editingTaskName.trim() || submitting}
                  >
                    <Check className={classes.checkIcon} />
                  </IconButton>
                  <IconButton
                    className={classes.iconButton}
                    onClick={handleCancelEdit}
                    disabled={submitting}
                  >
                    <Close className={classes.closeIcon} />
                  </IconButton>
                </Box>
              ) : (
                <>
                  <Typography className={classes.taskText}>
                    {task.tasksName}
                  </Typography>
                  {!isDisabled && (
                    <Box className={classes.actionButtons}>
                      <IconButton
                        className={classes.iconButton}
                        onClick={() => handleStartEdit(task)}
                        disabled={submitting}
                      >
                        <Edit className={classes.editIcon} />
                      </IconButton>
                      <IconButton
                        className={classes.iconButton}
                        onClick={() => setTaskToDelete(task)}
                        disabled={submitting}
                      >
                        <Delete className={classes.deleteIcon} />
                      </IconButton>
                    </Box>
                  )}
                </>
              )}
            </Box>
          ))}
        </Box>
      )}
      <AppConfirmDialog
        open={Boolean(taskToDelete)}
        onConfirm={async () => {
          if (taskToDelete) {
            const task = taskToDelete;
            setTaskToDelete(null);
            await handleDeleteTask(task);
          }
        }}
        onClose={() => setTaskToDelete(null)}
        title={t("title.deleteTask", "Delete Task")}
        content={t("text.deleteTaskConfirm", "Are you sure you want to delete this task?")}
      />
    </Box>
  );
};

ShiftTasksSection.propTypes = {
  jobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  activeAvailabilityIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  isDisabled: PropTypes.bool,
};

export default ShiftTasksSection;
